import React, { Fragment } from 'react';
import { useGenGridCol } from '../../../../../utils/ComponentHelper';
import { displayAmountCurrying } from '../../../../../utils';
import { useTranslation } from '../../../../../hooks';
import { ExecutionType, Operation } from '../types/OperationDetailsRequestBody';
import { getSwapPathDisplay } from '../utils/getSwapPathDisplay';
import { MultiSigRawTxDetail, MultiSigRawTx, GetListRes } from '../../types/GetListRes';
import { Button } from '../../../../../components/MuiGenerals';
import ExpandableText from '../components/ExpandableText';
import { useAppSelector } from '../../../../../reducer/hooks';
import { selectIsMobileView } from '../../../../../reducer/mediaSlice';
import { TransactionOperationTableRow } from '../types/TransactionOperationTable';
import { tronAddressToEthAddress } from '../../../../../utils/tronAddressToEthAddress';
import { TableRow } from '../../types/Table';
import CopyableText from '../../../../../components/CopyableText';
import ShowMoreButton from '../components/ShowMoreButton';
import useAssetInfosList from '../../../../../hooks/useAssetInfosList';
import useChains from '../../../../../hooks/useChains';

interface IProps {
  multiSigRawTxs: MultiSigRawTx[];
  selectedRow: TableRow;
  renderMultiSigRawTxsOperationCell: (params: any) => React.ReactNode;
}

interface MultiSigRawTxTableRow {
  id: number;
  batchId: number;
  // chainId: number;
  nonce: number;
  operationSequence: string;
  fromAddress: string;
  toAddress: string;
  fromAddressHex: string | string[];
  toAddressHex: string | string[];
  value: string;
  data: string;
  chainName: string;
  rawData: MultiSigRawTx;
}

const MultipleCopyableText = ({ value, row }: { value: string; row?: MultiSigRawTxTableRow }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { getAssetInfo } = useAssetInfosList();

  const addresses = value.split(',').map((addr) => addr.trim());

  const Address = (props: { address: string; tokenName: string }) => {
    const { address, tokenName } = props;

    return (
      <>
        <CopyableText text={address} amendedText={tokenName ? `(${tokenName})` : ''} />
      </>
    );
  };

  // handle multiple
  if (typeof value === 'string' && value.includes(',')) {
    const displayAddresses = addresses.map((addr: string, index: number) => {
      const assetInfo = getAssetInfo(row?.chainName || '', addr);

      return (
        <Fragment key={index}>
          <Address address={addr} tokenName={assetInfo?.name || ''} />
        </Fragment>
      );
    });

    return (
      <>
        {isExpanded ? displayAddresses : displayAddresses?.[0]}
        <div>
          <ShowMoreButton onClick={() => setIsExpanded((prev) => !prev)} isExpanded={isExpanded} />
        </div>
      </>
    );
  }

  return (
    <Address address={value} tokenName={getAssetInfo(row?.chainName || '', value)?.name || ''} />
  );
};

const useMultiSigRawTxsTable = ({
  selectedRow,
  multiSigRawTxs,
  renderMultiSigRawTxsOperationCell,
}: IProps) => {
  const { translate } = useTranslation();
  const isMobile = useAppSelector(selectIsMobileView);
  const { getChainInfo } = useChains();
  const isTron = Number(selectedRow?.rawData?.chainType) === 3;
  const isHideHexAddress = !isTron;

  const columns = [
    useGenGridCol('signature', {
      minWidth: 100,
      renderCell: renderMultiSigRawTxsOperationCell,
    }),
    useGenGridCol('operationSequence', {
      minWidth: 100,
    }),
    useGenGridCol('batchId', {
      minWidth: 100,
    }),
    // useGenGridCol('chainId', {
    //   minWidth: 100,
    // }),
    useGenGridCol('nonce', {
      minWidth: 100,
    }),

    useGenGridCol('fromAddress', {
      minWidth: 250,
    }),
    useGenGridCol('fromAddressHex', {
      minWidth: 300,
      hide: isHideHexAddress,
    }),
    useGenGridCol('toAddress', {
      minWidth: 250,
      renderCell: ({ value, row }) => <MultipleCopyableText value={value} row={row} />,
    }),
    useGenGridCol('toAddressHex', {
      minWidth: 300,
      hide: isHideHexAddress,
      renderCell: ({ value, row }) => <MultipleCopyableText value={value} row={row} />,
    }),
    useGenGridCol('value', {
      minWidth: 150,
      renderCell: ({ value }) => <ExpandableText text={value} maxChars={2} />,
    }),
    useGenGridCol('data', {
      minWidth: 300,
      renderCell: ({ value }) => <ExpandableText text={value} />,
    }),
  ];

  const splitLineIfStringArr = (value: string | string[]) => {
    // if array contains all empty string, return empty string
    if (Array.isArray(value) && value.every((item) => item.trim() === '')) {
      return '';
    }

    return typeof value === 'string' ? value : value.join(',\n');
  };

  const rows = multiSigRawTxs
    .map((item: MultiSigRawTx, index: number) => {
      const operationSequencesDisplay = item?.operation_seqs?.join(',') || '';
      const data = item?.multi_sig_raw_tx;
      const fromAddressHex = tronAddressToEthAddress(data?.from || '');
      const toAddressHex = tronAddressToEthAddress(data?.to || '');
      const displayData = splitLineIfStringArr(data?.data || '');

      const chainInfo = getChainInfo(
        selectedRow?.rawData?.chainType,
        selectedRow?.rawData?.chainId
      );
      const chainName = chainInfo?.chainName;

      const rows: MultiSigRawTxTableRow = {
        id: index,
        batchId: data?.batch_id,
        // chainId: data?.chain_id,
        nonce: data?.nonce,
        operationSequence: operationSequencesDisplay,
        fromAddress: data?.from,
        toAddress: splitLineIfStringArr(data?.to || ''),
        fromAddressHex: fromAddressHex || '',
        toAddressHex: splitLineIfStringArr(toAddressHex || ''),
        value: splitLineIfStringArr(data?.value || ''),
        data: displayData,
        chainName: chainName || '',
        rawData: item,
      };

      return rows;
    })
    .sort((a, b) => {
      // sort by operation sequence, then batchId, then nonce in ascending order

      const aSeq = a.operationSequence.split(',').map(Number);
      const bSeq = b.operationSequence.split(',').map(Number);

      for (let i = 0; i < Math.max(aSeq.length, bSeq.length); i++) {
        const aNum = aSeq[i] || 0;
        const bNum = bSeq[i] || 0;

        if (aNum !== bNum) {
          return aNum - bNum;
        }
      }

      if (a.batchId !== b.batchId) {
        return a.batchId - b.batchId;
      }

      return a.nonce - b.nonce;
    });

  return { multiSigRawTxsColumns: columns, multiSigRawTxsRows: rows };
};

export default useMultiSigRawTxsTable;
